/**
 * Role Eligibility Matrix
 *
 * Defines which roles can be granted which actions
 * This is a hard gate - ineligible roles CANNOT be granted permission
 * even if admin tries to create the grant
 */

import { AllOrganizationalRoles, Role } from './enums';
import { AuthorizableAction } from './authorizable-actions';

/**
 * Role eligibility matrix for secret actions
 *
 * Key: AuthorizableAction
 * Value: Array of roles that CAN be granted this action
 *
 * Transitional rules after removing capacity-specific org roles:
 * - INTERNAL_ADMIN retains broad admin visibility/edit eligibility
 * - PRINCIPAL and ADMIN retain org-admin secret-management eligibility
 * - REP retains minimal operational eligibility
 * - Capacity-specific constraints are enforced elsewhere via organization flags
 */
const AUTHORIZABLE_ACTION_ELIGIBILITY: Record<AuthorizableAction, Role[]> = {
    [AuthorizableAction.DISPLAY]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.LIST]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.CREATE]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.EDIT]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.DELETE]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN],
    [AuthorizableAction.USE]: [Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.ROTATE]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN, Role.REP],
    [AuthorizableAction.GRANT]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL],
    [AuthorizableAction.REVOKE]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL],
    [AuthorizableAction.TAKE_PAYMENT]: AllOrganizationalRoles,
    [AuthorizableAction.VOID_PAYMENT]: AllOrganizationalRoles,
    [AuthorizableAction.REFUND_PAYMENT]: AllOrganizationalRoles,
    [AuthorizableAction.OVERRIDE_FEE]: AllOrganizationalRoles,
    [AuthorizableAction.IMPERSONATE]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL],
    [AuthorizableAction.AUDIT]: [Role.INTERNAL_ADMIN, Role.PRINCIPAL, Role.ADMIN],
} as const;

/**
 * Check if a role is eligible for an action
 *
 * @param action - Action being checked
 * @returns true if role can be granted this action
 *
 * Note: SUPERADMIN always returns true (handled in caller)
 */
export function getEligibleRolesForAction(action: AuthorizableAction): readonly Role[] {
    return AUTHORIZABLE_ACTION_ELIGIBILITY[action] ?? [];
}

/**
 * Check if a role is eligible for an action
 *
 * @param role - User's role (from RoleAssignment)
 * @param action - Action being checked
 * @returns true if role can be granted this action
 *
 * Note: SUPERADMIN always returns true (handled in caller)
 */
export function isRoleEligibleForAction(
    role: Role,
    action: AuthorizableAction
    // TODO: Add ResourceType to the function signature
): boolean {
    try {
        return getEligibleRolesForAction(action).includes(role);
    } catch {
        // Unknown action - return false
        return false;
    }
}
